import {PasswordInput, Stack, TextInput} from '@mantine/core';
import React from 'react';

import McpUrlParamFields from 'nav/sidepanel/mcps/McpUrlParamFields';

import type {Extension} from '../../content-store/extensionConstants';
import type {ExtensionInstallStepProps, ExtensionInstallWizardStep} from '../registry';
import {patchMcpInstallContext, type McpExtensionInstallContext} from './mcpInstallContext';

export function shouldShowMcpUrlParamsStep(extension: Extension): boolean {
  const urlParams = extension.latestVersion?.mcpProperties?.urlParams ?? [];
  return urlParams.length > 0;
}

export function shouldShowMcpManualOAuthStep(extension: Extension): boolean {
  return extension.latestVersion?.mcpProperties?.requiresManualOauthSetup ?? false;
}

function McpUrlParamsWizardStep({
  extension,
  installContext,
  setInstallContext,
}: ExtensionInstallStepProps): JSX.Element {
  const mcpProperties = extension.latestVersion?.mcpProperties ?? null;
  const urlParams = mcpProperties?.urlParams ?? [];
  const serverUrlTemplate = mcpProperties?.serverUrl ?? null;
  const {urlParamValues} = installContext;

  return (
    <McpUrlParamFields
      urlParams={urlParams}
      urlParamValues={urlParamValues}
      onChange={(key, value) => {
        setInstallContext((prev) => patchMcpInstallContext(prev, {urlParamValues: {[key]: value}}));
      }}
      serverUrlTemplate={serverUrlTemplate}
    />
  );
}

function McpManualOAuthWizardStep({
  installContext,
  setInstallContext,
}: ExtensionInstallStepProps): JSX.Element {
  const {manualOAuthClientId, manualOAuthClientSecret} = installContext;

  return (
    <Stack spacing="md">
      <TextInput
        label="OAuth client ID"
        placeholder="Client ID"
        withAsterisk={true}
        value={manualOAuthClientId}
        onChange={(e) => {
          const value = e.currentTarget.value;
          setInstallContext((prev) => patchMcpInstallContext(prev, {manualOAuthClientId: value}));
        }}
        autoComplete="off"
      />
      <PasswordInput
        label="OAuth client secret"
        placeholder="Client secret"
        withAsterisk={true}
        value={manualOAuthClientSecret}
        onChange={(e) => {
          const value = e.currentTarget.value;
          setInstallContext((prev) => patchMcpInstallContext(prev, {manualOAuthClientSecret: value}));
        }}
        autoComplete="new-password"
      />
    </Stack>
  );
}

function mcpUrlParamsCanProceed(installContext: McpExtensionInstallContext, extension: Extension): boolean {
  const urlParams = extension.latestVersion?.mcpProperties?.urlParams ?? [];
  return urlParams.every((p) => Boolean(installContext.urlParamValues[p.key]?.trim()));
}

function mcpManualOAuthCanProceed(
  installContext: McpExtensionInstallContext,
  _extension: Extension
): boolean {
  return (
    installContext.manualOAuthClientId.trim() !== '' && installContext.manualOAuthClientSecret.trim() !== ''
  );
}

interface McpWizardStepDefinition extends ExtensionInstallWizardStep {
  /** Whether this step applies to the given extension (catalog-driven). */
  shouldShow: (extension: Extension) => boolean;
}

const MCP_WIZARD_STEP_DEFINITIONS: Array<McpWizardStepDefinition> = [
  {
    id: 'mcp-url-params',
    label: 'URL parameters',
    stepDescription:
      "This MCP's server URL requires further configuration. Enter the required URL parameters to construct the server URL.",
    shouldShow: shouldShowMcpUrlParamsStep,
    canProceed: mcpUrlParamsCanProceed,
    StepBody: McpUrlParamsWizardStep,
  },
  {
    id: 'mcp-manual-oauth',
    label: 'OAuth credentials',
    stepDescription:
      'This MCP server uses manual OAuth registration. Enter your pre-registered client ID and secret before installing.',
    shouldShow: shouldShowMcpManualOAuthStep,
    canProceed: mcpManualOAuthCanProceed,
    StepBody: McpManualOAuthWizardStep,
  },
];

export function getMcpInstallWizardSteps(extension: Extension): Array<ExtensionInstallWizardStep> {
  return MCP_WIZARD_STEP_DEFINITIONS.filter((def) => def.shouldShow(extension)).map(
    ({shouldShow: _shouldShow, ...step}) => step
  );
}
